import React from 'react';
import { AwardIcon, CheckCircle2Icon, ClipboardCheckIcon, GraduationCapIcon, MapIcon, PhoneIcon, QuoteIcon, ShieldCheckIcon, UsersRoundIcon, WrenchIcon } from 'lucide-react';
import { ARCHIVE_CONTACT, archiveConciergeModalities } from './archiveData';
const pillars = [{
  icon: CheckCircle2Icon,
  title: 'Select',
  desc: 'Evaluate what belongs in your office, how it serves patients, and whether it aligns with practice goals.'
}, {
  icon: GraduationCapIcon,
  title: 'Train',
  desc: 'Give your staff a clear path to confidently understand, introduce, and use the technology.'
}, {
  icon: WrenchIcon,
  title: 'Implement',
  desc: 'Build a practical plan for bringing the right modality into your day-to-day practice.'
}];
const depthItems = [{
  icon: ClipboardCheckIcon,
  title: 'Patient and protocol fit',
  desc: 'Compare the technology against the conditions, treatment goals, and visit flow your practice actually supports.'
}, {
  icon: UsersRoundIcon,
  title: 'Workflow inside the practice',
  desc: 'Consider room setup, staff time, treatment sequence, training needs, and how the modality fits into daily care.'
}, {
  icon: GraduationCapIcon,
  title: 'A rollout doctors can use',
  desc: 'Move from equipment selection to training, patient education, and confident implementation instead of letting a device sit underused.'
}];
const proofItems = [{
  icon: AwardIcon,
  text: '25+ years modality experience'
}, {
  icon: GraduationCapIcon,
  text: 'Training and implementation support'
}, {
  icon: ShieldCheckIcon,
  text: 'Curated products, not hype-driven recommendations'
}, {
  icon: MapIcon,
  text: 'Serving practices across the United States'
}];
export function ArchiveSupport() {
  return <>
      <section className="w-full bg-white py-20 sm:py-24" data-id="element-229" data-mp="2l2bpzz8kbfub">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-230" data-mp="2g41wru6eepbc">
          <div className="mx-auto max-w-2xl text-center" data-id="element-231" data-mp="2g5d2w0nbg95m">
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal" data-id="element-232" data-mp="ef4hl4fe58qi">
              The JAG Approach
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-[2.4rem]" data-id="element-233" data-mp="j7fhw8e1mwjl">
              Every Practice is Different. Your Technology Should Be Too
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-charcoal/70" data-id="element-234" data-mp="9dyjss6viva4">
              JAG helps doctors evaluate what belongs in their office, how it
              serves patients, how staff will use it, and how it can be
              implemented profitably.
            </p>
          </div>
          <div className="mx-auto mt-14 grid max-w-5xl gap-7 md:grid-cols-3" data-id="element-235" data-mp="na2awcmganid">
            {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return <div key={pillar.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-id="element-236" data-mp="3801fcc4aeeg8" data-mpi={index}>
                  <div className="flex items-center gap-3" data-id="element-237" data-mp="1uybwrx2ci2m8">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white" data-id="element-238" data-mp="1q0k95f8qh2nu">
                      <Icon className="h-5 w-5" data-id="element-239" data-mp="240l82p5fu00o" />
                    </span>
                    <span className="font-display text-3xl font-extrabold text-slate-200" data-id="element-240" data-mp="1zyhvtyay2rec">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold text-navy" data-id="element-241" data-mp="1c95h6iwulr4n">
                    {pillar.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/75" data-id="element-242" data-mp="1l0z18fbl5jyz">
                    {pillar.desc}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      <section id="used-equipment" className="w-full bg-slate-50 py-20 sm:py-24" data-id="element-243" data-mp="sjv133t6x460">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-244" data-mp="oijihi8qhj7b">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_50px_-28px_rgba(26,27,75,0.28)] sm:p-12" data-id="element-245" data-mp="118twpt49qtne">
            <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-246" data-mp="1686fa1o3xek6">
              <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-50" data-id="element-247" data-mp="1b5y7kwsnz2ow">
                <img src="https://asset.imagine.art/video/thumbnails/23882006-c9b8-4272-9538-62b6c951ddca" alt="Pre-owned therapeutic modality equipment being evaluated in a clean clinic" className="h-full w-full object-cover" data-id="element-248" data-mp="1fxq3znyi42my" />
              </div>
              <div data-id="element-249" data-mp="1tyiwy495mpny">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal" data-id="element-250" data-mp="1ty0vmt7mxl96">
                  USED EQUIPMENT
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-[2.4rem]" data-id="element-251" data-mp="3vp487o1359wd">
                  Pre-owned modalities, evaluated before you buy
                </h2>
                <p className="mt-5 text-[16px] leading-relaxed text-charcoal/75" data-id="element-252" data-mp="3t57saydyhgiv">
                  When the right system becomes available, JAG helps doctors
                  look beyond price alone. We help compare the equipment type,
                  intended use, condition questions, serviceability, training
                  needs, and whether the opportunity actually fits the practice.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/60" data-id="element-253" data-mp="fx4e2hc9q0oz">
                  Availability changes often, so the first step is a
                  conversation. Ask JAG what is currently worth considering
                  across laser, shockwave, light therapy, imaging, and other
                  modality categories.
                </p>
                <a href={ARCHIVE_CONTACT.phoneHref} className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2" data-id="element-254" data-mp="2qq7825knavfn">
                  <PhoneIcon className="h-[18px] w-[18px]" data-id="element-255" data-mp="1hqmvloqc98p6" />
                  Call About Used Equipment
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-white py-20 sm:py-24" data-id="element-256" data-mp="18u5i4eui4yq9">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-257" data-mp="u23lr4rn4nuz">
          <div className="mx-auto max-w-2xl text-center" data-id="element-258" data-mp="ytv4wu8gvrb3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal" data-id="element-259" data-mp="yvfyagj1p47d">
              BEFORE YOU INVEST
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-[2.4rem]" data-id="element-260" data-mp="q1kupcxknltu">
              Know what the spec sheet won’t tell you
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-charcoal/70" data-id="element-261" data-mp="g3dq7tkgyw8q">
              Manufacturer specs matter, but they are only part of the decision.
              JAG helps practices compare how a modality fits patient demand,
              treatment flow, staff training, space, and long-term use in the
              office.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3" data-id="element-262" data-mp="6h2qkmho0mpa">
            {depthItems.map(item => {
            const Icon = item.icon;
            return <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-7" data-id="element-263" data-mp="ffo4g4c6zdjq">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal" data-id="element-264" data-mp="bd1jmndxusw1">
                    <Icon className="h-5 w-5" data-id="element-265" data-mp="k769p4l900ai" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-navy" data-id="element-266" data-mp="k99749oqcvp9">
                    {item.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-charcoal/70" data-id="element-267" data-mp="u5lvcs1iahcm">
                    {item.desc}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      <section id="concierge" className="w-full bg-navy py-20 text-white sm:py-24" data-id="element-268" data-mp="3fwgwz398r9e8">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-269" data-mp="3kxwj6yn5nly7">
          <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]" data-id="element-270" data-mp="3pvel452r90wp">
            <div data-id="element-271" data-mp="3ptbv3l1lr7rz">
              <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal-light" data-id="element-272" data-mp="2s862chu525ye">
                CONCIERGE MODALITY SUITE
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-[2.4rem]" data-id="element-273" data-mp="2x7ahdp3quxwn">
                Build the right modality mix around your practice
              </h2>
              <p className="mt-5 text-[16px] leading-relaxed text-white/80" data-id="element-274" data-mp="36y54evote1j5">
                The Concierge Modality Suite is JAG’s consultative planning
                process for practices considering more than one technology.
                Start with patient needs, current services, staff workflow,
                space, and growth goals, then compare which modalities belong
                together and which should wait.
              </p>
              <a href={ARCHIVE_CONTACT.phoneHref} className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-teal-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy" data-id="element-275" data-mp="mk71d28ddl8y">
                <PhoneIcon className="h-[18px] w-[18px]" data-id="element-276" data-mp="3tz4h1v9jr17" />
                Call About the Suite
              </a>
            </div>
            <div className="relative grid grid-cols-2 gap-4 sm:grid-cols-3" data-id="element-277" data-mp="3v1l7csa5rph0">
              {archiveConciergeModalities.map(modality => <div key={modality.name} className="rounded-2xl border border-white/15 bg-white/[0.07] p-4 transition-colors hover:border-teal/60 hover:bg-white/[0.1]" data-id="element-278" data-mp="3q39us9shb0v3">
                  <div className="mb-3 h-1 w-8 rounded-full bg-teal" data-id="element-279" data-mp="2t86xn6wa47om" />
                  <p className="font-display text-[14px] font-bold leading-tight text-white" data-id="element-280" data-mp="2t5ujz5xk0jhd">
                    {modality.name}
                  </p>
                  <p className="mt-1.5 text-[12px] leading-snug text-white/70" data-id="element-281" data-mp="32yt8yowyqc1i">
                    {modality.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-slate-50 py-20 sm:py-24" data-id="element-282" data-mp="2aaxdton1ks6u">
        <div className="mx-auto max-w-4xl px-5 sm:px-8" data-id="element-283" data-mp="2o9x38yidpb42">
          <p className="text-center text-[12px] font-semibold uppercase tracking-[0.16em] text-teal" data-id="element-284" data-mp="2j9r7b652t2g6">
            Provider Perspective
          </p>
          <figure className="mt-5 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-24px_rgba(26,27,75,0.25)] sm:p-12" data-id="element-285" data-mp="3hbrn0hkd6oi">
            <QuoteIcon className="h-9 w-9 text-teal/50" data-id="element-286" data-mp="3uragdf5c7zmn" />
            <blockquote className="mt-6 font-display text-2xl font-medium leading-snug text-navy sm:text-[28px]" data-id="element-287" data-mp="6zrd40cmnqko">
              “Working with JAG felt less like buying equipment and more like
              adding a partner to our team. The guidance, training, and
              follow-through made the modality something our whole practice
              could actually use with confidence.”
            </blockquote>
            <figcaption className="mt-8 flex items-center gap-4" data-id="element-288" data-mp="prk3ro484zp8">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy font-display text-lg font-bold text-white" data-id="element-289" data-mp="uqeo4uhuawbg">
                DC
              </span>
              <div data-id="element-290" data-mp="l026ba1eth90">
                <p className="font-semibold text-navy" data-id="element-291" data-mp="kzk3fuk8s4s4">Dr. Don Casey</p>
                <p className="text-[13px] text-charcoal/70" data-id="element-292" data-mp="18q68dcyy98xw">Practice Owner</p>
              </div>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="w-full border-y border-slate-100 bg-slate-50/60" data-id="element-293" data-mp="1diply9xn042p">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-slate-100 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4 lg:divide-x lg:divide-y-0" data-id="element-294" data-mp="1nba89as15czk">
          {proofItems.map(({
          icon: Icon,
          text
        }) => <div key={text} className="flex items-start gap-3 px-0 py-6 lg:px-6" data-id="element-295" data-mp="1dcm67lgfx8hi">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal/10 text-teal" data-id="element-296" data-mp="19c29uk3oy8wf">
                <Icon className="h-[18px] w-[18px]" data-id="element-297" data-mp="14f2284r70y37" />
              </span>
              <p className="text-[13.5px] font-medium leading-snug text-charcoal" data-id="element-298" data-mp="zlzfogku2owi">
                {text}
              </p>
            </div>)}
        </div>
      </section>
    </>;
}